import { useId, useState, type FormEvent } from 'react';
import { curso } from '@curso/content/curso';
import { PaginaAvulsa } from '@inscricao/components/PaginaAvulsa';
import { confirmarSaida, pedirSaida } from '@inscricao/services/contatos';

/**
 * Pagina /sair: o canal para a pessoa sair da lista sozinha.
 *
 * Dois caminhos:
 * - com codigo (/sair?t=...), vindo do link "Sair da lista" de um e-mail:
 *   mostra um botao de confirmacao. A saida nao acontece so por abrir o link,
 *   porque alguns leitores de e-mail abrem links sozinhos para checar
 *   seguranca, e isso tiraria gente da lista sem ela pedir;
 * - sem codigo: a pessoa digita o e-mail e recebe o link de confirmacao. A
 *   resposta e sempre a mesma, para a pagina nao revelar quem esta cadastrado.

 */
export function PaginaSaida() {
  const token = new URLSearchParams(window.location.search).get('t');

  return (
    <PaginaAvulsa titulo="Sair da lista">
      {token ? <Confirmar token={token} /> : <Pedir />}
    </PaginaAvulsa>
  );
}

const botao =
  'min-h-11 bg-tinta px-6 text-miudo font-medium text-papel transition-colors hover:bg-destaque disabled:opacity-60';

function Confirmar({ token }: { token: string }) {
  const [situacao, setSituacao] = useState<'esperando' | 'enviando' | 'saiu' | 'erro'>('esperando');

  async function sair() {
    setSituacao('enviando');
    setSituacao(await confirmarSaida(token));
  }

  if (situacao === 'saiu') {
    return (
      <div role="status">
        <p className="display-item">Pronto, você saiu da lista</p>
        <p className="mt-3 text-apoio leading-relaxed text-tinta-suave">
          Seus dados foram apagados e você não vai receber mais avisos da {curso.formato}. Se
          mudar de ideia, é só se cadastrar de novo no site.
        </p>
      </div>
    );
  }

  return (
    <div>
      <p className="text-apoio leading-relaxed text-tinta-suave">
        Ao confirmar, seu nome e seu contato são apagados e você deixa de receber avisos da{' '}
        {curso.formato}.
      </p>
      {situacao === 'erro' ? (
        <p role="alert" className="mt-4 text-miudo leading-relaxed text-tinta">
          Não deu para concluir agora. Tente de novo em alguns minutos.
        </p>
      ) : null}
      <button type="button" onClick={sair} disabled={situacao === 'enviando'} className={`${botao} mt-6`}>
        {situacao === 'enviando' ? 'Saindo…' : 'Confirmar saída'}
      </button>
    </div>
  );
}

function Pedir() {
  const base = useId();
  const [email, setEmail] = useState('');
  const [situacao, setSituacao] = useState<'preenchendo' | 'enviando' | 'pedido' | 'erro'>('preenchendo');

  async function enviar(evento: FormEvent) {
    evento.preventDefault();
    setSituacao('enviando');
    setSituacao(await pedirSaida(email.trim().toLowerCase()));
  }

  if (situacao === 'pedido') {
    return (
      <div role="status">
        <p className="display-item">Confira seu e-mail</p>
        <p className="mt-3 text-apoio leading-relaxed text-tinta-suave">
          Se este endereço estiver na lista, você vai receber em instantes um e-mail com o botão
          para confirmar a saída. Se não aparecer, confira a caixa de spam.
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={enviar}>
      <p className="text-apoio leading-relaxed text-tinta-suave">
        Digite o e-mail que você cadastrou. Enviamos um link para você confirmar a saída.
      </p>
      <label htmlFor={`${base}-email`} className="sr-only">
        Seu e-mail
      </label>
      <input
        id={`${base}-email`}
        type="email"
        required
        maxLength={254}
        autoComplete="email"
        value={email}
        onChange={(evento) => setEmail(evento.target.value)}
        placeholder="seu@email.com"
        className="mt-5 w-full border-b border-tinta-fraca bg-transparent px-1 py-2.5 text-apoio text-tinta placeholder:text-tinta-fraca focus:border-tinta focus:outline-none"
      />
      {situacao === 'erro' ? (
        <p role="alert" className="mt-4 text-miudo leading-relaxed text-tinta">
          Não deu para enviar agora. Tente de novo em alguns minutos.
        </p>
      ) : null}
      <button type="submit" disabled={situacao === 'enviando'} className={`${botao} mt-6`}>
        {situacao === 'enviando' ? 'Enviando…' : 'Enviar link de saída'}
      </button>
    </form>
  );
}
